import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import './responsive.css';
import Header from './components/Header';
import Home from './pages/Home';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { publicTheme } from './theme/publicTheme';

const Footer = lazy(() => import('./components/Footer'));
const FacebookChat = lazy(() => import('./components/FacebookChat'));

const Appointments = lazy(() => import('./pages/Appointments'));
const Articles = lazy(() => import('./pages/Articles'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const GalleryDetails = lazy(() => import('./pages/GalleryDetails'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const DoctorProfile = lazy(() => import('./pages/DoctorProfile'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const LalitpurDentalClinic = lazy(() => import('./pages/locations/LalitpurDentalClinic'));
const TeethCleaningNepal = lazy(() => import('./pages/locations/TeethCleaningNepal'));
const DentalImplantsNepal = lazy(() => import('./pages/locations/DentalImplantsNepal'));
const TeethWhiteningNepal = lazy(() => import('./pages/locations/TeethWhiteningNepal'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const DynamicPageView = lazy(() => import('./pages/DynamicPageView'));

const AdminSignInPage = lazy(() => import('./admin/pages/AdminSignInPage'));
const AdminLayout = lazy(() => import('./admin/layout/AdminLayout'));
const ProtectedRoute = lazy(() => import('./admin/components/ProtectedRoute'));
const AdminDashboardPage = lazy(() => import('./admin/pages/AdminDashboardPage'));
const AdminArticlesPage = lazy(() => import('./admin/pages/AdminArticlesPage'));
const AdminArticleDetailPage = lazy(() => import('./admin/pages/AdminArticleDetailPage'));
const AdminArticleFormPage = lazy(() => import('./admin/pages/AdminArticleFormPage'));
const AdminServicesPage = lazy(() => import('./admin/pages/AdminServicesPage'));
const AdminServiceDetailPage = lazy(() => import('./admin/pages/AdminServiceDetailPage'));
const AdminServiceFormPage = lazy(() => import('./admin/pages/AdminServiceFormPage'));
const AdminGalleryPage = lazy(() => import('./admin/pages/AdminGalleryPage'));
const AdminGalleryDetailPage = lazy(() => import('./admin/pages/AdminGalleryDetailPage'));
const AdminAppointmentsPage = lazy(() => import('./admin/pages/AdminAppointmentsPage'));
const AdminMessagesPage = lazy(() => import('./admin/pages/AdminMessagesPage'));
const AdminMediaLibraryPage = lazy(() => import('./admin/pages/AdminMediaLibraryPage'));
const AdminPagesPage = lazy(() => import('./admin/pages/AdminPagesPage'));
const AdminPageFormPage = lazy(() => import('./admin/pages/AdminPageFormPage'));
const AdminSettingsPage = lazy(() => import('./admin/pages/AdminSettingsPage'));
const AdminSeoPage = lazy(() => import('./admin/pages/AdminSeoPage'));
const AdminSeoFormPage = lazy(() => import('./admin/pages/AdminSeoFormPage'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/** Remove crawler-only HTML injected outside #root (e.g. build/services/index.html). */
const HideSeoFallbacks = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Detail pages own their fallback until content loads or a real 404 arrives.
    if (/^\/(articles|services)(\/|$)/.test(pathname)) return;
    document.querySelectorAll('[data-seo-fallback="true"]').forEach((el) => {
      el.remove();
    });
  }, [pathname]);

  return null;
};

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', width: '100%' }}>
    <CircularProgress sx={{ color: '#35bcb3' }} size={32} />
  </Box>
);

const isAdminRoute = (pathname: string) =>
  pathname === '/login' || pathname.startsWith('/admin');

const AppRoutes = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      pathname !== '/' &&
      !pathname.startsWith('/admin') &&
      !pathname.startsWith('/login') &&
      !pathname.endsWith('/')
    ) {
      navigate(pathname + '/', { replace: true });
    }
  }, [pathname, navigate]);

  const adminMode = isAdminRoute(pathname);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: adminMode ? '#f5f7fa' : '#f5f5f5',
      }}
    >
      {!adminMode && <Header />}
      <main style={{ flex: 1, paddingTop: adminMode ? 0 : '70px' }}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/appointments/" element={<Appointments />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/" element={<Articles />} />
            <Route path="/articles/:id" element={<ArticleDetail key={pathname} />} />
            <Route path="/articles/:id/" element={<ArticleDetail key={pathname} />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery/" element={<Gallery />} />
            <Route path="/gallery/:id" element={<GalleryDetails />} />
            <Route path="/gallery/:id/" element={<GalleryDetails />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/" element={<Services />} />
            <Route path="/services/:id" element={<ServiceDetail key={pathname} />} />
            <Route path="/services/:id/" element={<ServiceDetail key={pathname} />} />
            <Route path="/doctors/:id" element={<DoctorProfile />} />
            <Route path="/doctors/:id/" element={<DoctorProfile />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/terms/" element={<Terms />} />
            <Route path="/locations/lalitpur-dental-clinic" element={<LalitpurDentalClinic />} />
            <Route path="/locations/lalitpur-dental-clinic/" element={<LalitpurDentalClinic />} />
            <Route path="/locations/teeth-cleaning-nepal" element={<TeethCleaningNepal />} />
            <Route path="/locations/teeth-cleaning-nepal/" element={<TeethCleaningNepal />} />
            <Route path="/locations/dental-implants-nepal" element={<DentalImplantsNepal />} />
            <Route path="/locations/dental-implants-nepal/" element={<DentalImplantsNepal />} />
            <Route path="/locations/teeth-whitening-nepal" element={<TeethWhiteningNepal />} />
            <Route path="/locations/teeth-whitening-nepal/" element={<TeethWhiteningNepal />} />
            <Route path="/unsubscribe/:email" element={<Unsubscribe />} />
            <Route path="/unsubscribe/:email/" element={<Unsubscribe />} />

            <Route path="/login" element={<AdminSignInPage />} />
            <Route path="/admin" element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="articles" element={<AdminArticlesPage />} />
                <Route path="articles/new" element={<AdminArticleFormPage />} />
                <Route path="articles/:id/edit" element={<AdminArticleFormPage />} />
                <Route path="articles/:id" element={<AdminArticleDetailPage />} />
                <Route path="services" element={<AdminServicesPage />} />
                <Route path="services/new" element={<AdminServiceFormPage />} />
                <Route path="services/:id/edit" element={<AdminServiceFormPage />} />
                <Route path="services/:id" element={<AdminServiceDetailPage />} />
                <Route path="gallery" element={<AdminGalleryPage />} />
                <Route path="gallery/:id" element={<AdminGalleryDetailPage />} />
                <Route path="appointments" element={<AdminAppointmentsPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="media" element={<AdminMediaLibraryPage />} />
                <Route path="pages" element={<AdminPagesPage />} />
                <Route path="pages/new" element={<AdminPageFormPage />} />
                <Route path="pages/:id/edit" element={<AdminPageFormPage />} />
                <Route path="seo" element={<AdminSeoPage />} />
                <Route path="seo/new" element={<AdminSeoFormPage />} />
                <Route path="seo/:id/edit" element={<AdminSeoFormPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<DynamicPageView />} />
          </Routes>
        </Suspense>
      </main>
      {!adminMode && (
        <Suspense fallback={null}>
          <Footer />
          <FacebookChat />
        </Suspense>
      )}
    </div>
  );
};

function App() {
  return (
    <SnackbarProvider>
      <AuthProvider>
        <SettingsProvider>
          <ThemeProvider theme={publicTheme}>
            <CssBaseline />
            <Router>
              <ScrollToTop />
              <HideSeoFallbacks />
              <AppRoutes />
            </Router>
          </ThemeProvider>
        </SettingsProvider>
      </AuthProvider>
    </SnackbarProvider>
  );
}

export default App;
